import { useEffect, useRef, useState } from 'react';
import { Box, Card, Tooltip, CollapsibleCard, toBool, withStyles, useForm, allCardBrands, FieldData } from 'vopay';
import { ElinxInfo, Contact, ElinxType, ElinxInfoSerialized } from './types';
import ElinxSettingsDialogue from '../ElinxSettings/ElinxSettingsDialogue';
import ElinxPreview from '../ElinxPreview/ElinxPreview';
import useFetchElinxSettings from '@/hooks/useApi/useFetchElinxSettings';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchContacts from '@/hooks/useApi/useFetchContacts';
import useFetchContactPaymentMethod from '@/hooks/useApi/useFetchContactPaymentMethod';
import {
  useElinxBasicDetailsRows,
  useElinxContactAddressRows,
  useElinxDetailsRows,
  useElinxPaymentSettingsRows,
  useBankAccountRows,
  useCardSettingsRows,
  useElinxRowsContactDetails,
  useElinxInstallmentPlansRows,
  useElinxPartialPaymentRows,
  useElinxSettingsRows,
  mapToPaymentSelectionValue,
  getDefaultPaymentSelectionMethods,
  getDefaultBankingPaymentRails,
} from './buildElinxRows.helper';
import styles from './ElinxPages.module.scss';
import useFetchClientAccountList from '@/hooks/useApi/useFetchClientAccountList';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { Button, UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';

export type ElinxFormProps = {
  formType: ElinxType;
  onSubmit: (values: ElinxInfoSerialized) => void;
};
const classes = withStyles(styles);

const ElinxForm: React.FC<ElinxFormProps> = ({ formType, onSubmit }) => {
  const alertDialog = useAlertDialog();
  const {
    response: { Country, USDEFTCollectEnabled, USDEFTSendEnabled, AccountName, clientAccountsEnabled },
  } = useClientData();
  const [, , , fetchContacts] = useFetchContacts({});
  const [, , , fetchClientAccounts] = useFetchClientAccountList({});

  const { paymentMethods, fetchPaymentMethods } = useFetchContactPaymentMethod();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form' });

  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 1);
  const defaultValues: ElinxInfo = {
    Contact: '',
    Currency: Country === 'US' ? 'USD' : 'CAD',
    Amount: '0',
    Frequency: 'single',
    NameOfFrequency: 'monthly',
    ScheduleStartDate: Date.now().toString(),
    ScheduleEndDate: endDate.getTime().toString(),
    Ending: 'Payments',
    TransactionLabel: '',
    Note: '',
    ContactType: 'individual',
    ExistingContactName: '',
    FirstName: '',
    LastName: '',
    BusinessName: '',
    NewEmailAddress: '',
    AddressLine1: '',
    AddressLine2: '',
    City: '',
    Province: '',
    PostalCode: '',
    Country: Country || 'CA',
    PaymentMethodDisplayOptions: undefined,
    PaymentSelectionMethod: '',
    AccountSelectionMethod: '',
    SenderName: AccountName,
    RecipientMessage: '',
    InvoiceNumber: '',
    InvoiceName: '',
    InvoiceFile: '',
    ScheduleRemainingAmount: false,
    FlexiblePaymentOptions: '',
    RemainderPaymentDate: endDate.getTime().toString(),
    ReceiverPhoneNumber: '',
    SendNotification: false,
    SendReminder: false,
    ReminderFrequency: '7',
    NotificationMethod: 'email',
    AcceptedCardBrands: allCardBrands.join(','),
    CardTypeValidation: false,
    AdditionalRecipientEmails: [],
  };

  const [isVisible, setIsVisible] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isUnderReview, setIsUnderReview] = useState(false);

  const [, , , fetchElinxSettings] = useFetchElinxSettings({});
  const [showingMore, setShowingMore] = useState(false);
  const [isNewContact, setIsNewContact] = useState(false);
  const [hasMultiplePaymentMethods, setHasMultiplePaymentMethods] = useState(false);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [isInstallmentPlan, setIsInstallmentPlan] = useState(false);
  const lockedFeatures = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const { permissions } = usePermissions();

  async function getContacts(searchTerm: string) {
    if (clientAccountsEnabled) {
      const res = await fetchClientAccounts({
        Limit: 100,
        SearchText: searchTerm,
      });

      if (!res.Success) {
        alertDialog.error(t('Dialogs.FetchClientAccountsError.title'), t('Dialogs.FetchClientAccountsError.content'), {
          abortLabel: t('Dialogs.FetchClientAccountsError.abortLabel'),
        });

        console.error(res.ErrorMessage);

        return;
      }

      setContacts(
        res.ClientAccounts?.map((c) => ({
          id: c.ClientAccountID,
          name: c.ClientName,
          email: c.EmailAddress,
          phone: c.PhoneNumber,
          accountType: c.IsBusiness ? 'business' : 'individual',
          additionalContactEmails: c.AdditionalContacts.email || [],
        })) || [],
      );
    } else {
      const res = await fetchContacts({
        Limit: 100,
        SearchText: searchTerm === '' ? undefined : searchTerm,
      });

      if (!res.Success) {
        alertDialog.error(t('Dialogs.FetchContactsError.title'), t('Dialogs.FetchContactsError.content'), {
          abortLabel: t('Dialogs.FetchContactsError.abortLabel'),
        });

        console.error(res.ErrorMessage);

        return;
      }

      setContacts(
        res.Contacts?.map((c) => ({
          id: c.ContactID,
          name: c.ContactName,
          email: c.EmailAddress,
          phone: c.PhoneNumber,
          accountType: c.IsBusiness ? 'business' : 'individual',
          additionalContactEmails: c.AdditionalContacts.email || [],
        })) || [],
      );
    }
  }

  useEffect(() => {
    getContacts('');
  }, []);

  const basicDetailsRows = useElinxBasicDetailsRows(
    contacts,
    getContacts,
    showingMore,
    formType,
    Country,
    (formType === 'Pay' && toBool(USDEFTSendEnabled)) ||
      (formType === 'Collect' && toBool(USDEFTCollectEnabled)) ||
      (formType === 'Connect' && (toBool(USDEFTSendEnabled) || toBool(USDEFTCollectEnabled))),
  );
  const contactAddressRows = useElinxContactAddressRows(Country);
  const detailsRows = useElinxDetailsRows(formType);

  const [contactName, setContactName] = useState('');

  const paymentSettingsRows = useElinxPaymentSettingsRows(formType, Country, contactName, paymentMethods);
  const bankAccountRows = useBankAccountRows(formType, Country);
  const contactDetailsRows = useElinxRowsContactDetails();
  const installmentPlansRows = useElinxInstallmentPlansRows(formType, isInstallmentPlan);
  const partialPaymentRows = useElinxPartialPaymentRows(formType, isPartialPayment);
  const cardSettingsRows = useCardSettingsRows();
  const settingsRows = useElinxSettingsRows(isUnderReview, defaultValues.NotificationMethod || 'email', contacts);
  const fieldDataList: FieldData<ElinxInfo>[] = [
    ...basicDetailsRows,
    ...contactDetailsRows,
    ...contactAddressRows,
    ...paymentSettingsRows,
    ...bankAccountRows,
    ...cardSettingsRows,
    ...partialPaymentRows,
    ...installmentPlansRows,
    ...detailsRows,
    ...settingsRows,
  ];

  const { renderFormElements, ctx } = useForm<ElinxInfo, ElinxInfoSerialized>({
    config: fieldDataList,
    initialValues: defaultValues,
    onSubmit: (serializedData, ctx) => {
      const accountType = contacts.find((contact) => contact.id === ctx.values.Contact)?.accountType;

      onSubmit({
        ...serializedData,
        FlexiblePaymentOptions: [
          hasMultiplePaymentMethods ? 'multiple' : '',
          isPartialPayment ? 'partial' : '',
          isInstallmentPlan ? 'installments' : '',
        ]
          .filter((option) => option !== '')
          .join(','),
        ExistingContactName: ctx.values.ExistingContactName,
        ReceiverPhoneNumber: ctx.values.ReceiverPhoneNumber,
        AccountHolderType: accountType,
        InvoiceName: ctx.values.InvoiceName,
        AdditionalRecipientEmails: serializedData.AdditionalRecipientEmails || [],
      });
    },
    isReviewing: isUnderReview,
  });

  useEffect(() => {
    const isNew = ctx.values.Contact === 'new';
    setIsNewContact(isNew);

    if (isNew) {
      const name =
        ctx.values.ContactType === 'business'
          ? ctx.values.BusinessName
          : [ctx.values.FirstName, ctx.values.LastName].filter(Boolean).join(' ');
      setContactName(name || '');
    } else {
      setContactName(ctx.values.ExistingContactName || '');
    }
  }, [
    ctx.values.Contact,
    ctx.values.ContactType,
    ctx.values.ExistingContactName,
    ctx.values.FirstName,
    ctx.values.LastName,
    ctx.values.BusinessName,
  ]);

  useEffect(() => {
    if ((ctx.values.PaymentSelectionMethod || '').split(',').length <= 1) {
      setHasMultiplePaymentMethods(false);
    }
  }, [ctx.values.PaymentSelectionMethod]);

  useEffect(() => {
    if (
      ctx.values.PaymentSelectionMethod &&
      ctx.values.PaymentSelectionMethod !== '' &&
      paymentMethods &&
      ctx.values.PaymentMethodDisplayOptions?.split(',').includes('Existing')
    ) {
      const filteredPaymentMethods = paymentMethods.filter(
        (paymentMethod) =>
          ctx.values.PaymentSelectionMethod?.includes(mapToPaymentSelectionValue(paymentMethod.PaymentMethod)) ||
          !ctx.values.PaymentSelectionMethod ||
          ctx.values.PaymentSelectionMethod === '',
      );
      if (filteredPaymentMethods.length === 0) {
        ctx.setFieldValue('PaymentMethodDisplayOptions', 'New');
      }
    }
  }, [ctx.values.PaymentSelectionMethod, paymentMethods]);

  //TODO: Move this into to the row file with onchange
  useEffect(() => {
    // Only set defaults if fields are completely empty
    if (!ctx.values.PaymentMethodDisplayOptions) {
      ctx.setFieldValue('PaymentSelectionMethod', '');
      ctx.setFieldValue('BankingPaymentRail', '');
      ctx.setFieldTouched('PaymentSelectionMethod', false);
      ctx.setFieldTouched('BankingPaymentRail', false);

      return;
    }

    const defaultPaymentMethods = getDefaultPaymentSelectionMethods(
      formType,
      Country,
      lockedFeatures.LockedSelectOptions,
      ctx.values.ScheduleStartDate,
      ctx.values.Frequency,
      paymentMethods,
      ctx.values.PaymentMethodDisplayOptions,
    );
    if (defaultPaymentMethods) {
      ctx.setFieldValue('PaymentSelectionMethod', defaultPaymentMethods);
    }

    const defaultBankingRails = getDefaultBankingPaymentRails(
      formType,
      Country,
      lockedFeatures.LockedSelectOptions,
      ctx.values.ScheduleStartDate,
      ctx.values.Frequency,
    );
    if (defaultBankingRails) {
      ctx.setFieldValue('BankingPaymentRail', defaultBankingRails);
    }
  }, [ctx.values.ScheduleStartDate, ctx.values.Frequency, paymentMethods, ctx.values.PaymentMethodDisplayOptions]);

  useEffect(() => {
    if (ctx.values.Contact && ctx.values.Contact !== 'new') {
      const existingPhoneNumber = contacts.find((contact) => contact.id === ctx.values.Contact)?.phone;

      if (existingPhoneNumber?.length === 11 && existingPhoneNumber.startsWith('1')) {
        ctx.setFieldValue('ReceiverPhoneNumber', existingPhoneNumber);
      } else {
        ctx.setFieldValue('ReceiverPhoneNumber', existingPhoneNumber);
      }
      fetchPaymentMethods(ctx.values.Contact);
    } else {
      ctx.setFieldValue('ReceiverPhoneNumber', '');
    }
  }, [ctx.values.Contact]);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ctx.setFieldValue(
      'FlexiblePaymentOptions',
      [
        hasMultiplePaymentMethods ? 'multiple' : '',
        isPartialPayment ? 'partial' : '',
        isInstallmentPlan ? 'installments' : '',
      ]
        .filter((option) => option !== '')
        .join(','),
    );
  }, [hasMultiplePaymentMethods, isPartialPayment, isInstallmentPlan]);

  const getElinxSettings = async () => {
    try {
      const res = await fetchElinxSettings({});
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      } else {
        setIsVisible(false);

        ctx.setFieldValue('SendNotification', toBool(res.ELinxSettings.ReceiverNotificationsEnabled));
        ctx.setFieldValue('SendReminder', toBool(res.ELinxSettings.ELinxReminderEnabled));
        ctx.setFieldValue('ReminderFrequency', res.ELinxSettings.ELinxReminderFrequency);
        ctx.setFieldValue('NotificationMethod', res.ELinxSettings.DefaultNotificationMethod);
      }
    } catch (err) {
      console.error(err);
      alertDialog.caution(t('Dialogs.FetchElinxSettingsError.title'), t('Dialogs.FetchElinxSettingsError.content'), {
        abortLabel: t('Dialogs.FetchElinxSettingsError.abortLabel'),
      });
    }
  };

  useEffect(() => {
    getElinxSettings();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        width: '100%',
        // when scrolling to the top of this element, add extra scroll beyond the top so we can also see the page title
        scrollMarginTop: '500px',
      }}
    >
      <form onSubmit={ctx.handleSubmit}>
        <Box flx="col" base="8" gap="lg">
          <Card>
            <Box gap="lg" base="8" flx="col">
              <div className={classes('grid')}>{renderFormElements({ config: basicDetailsRows })}</div>

              {!showingMore && (
                <Box jc="fs">
                  <UtilButton
                    onClick={() => {
                      setShowingMore(true);
                    }}
                    iconLeft="add"
                  >
                    {t('BasicDetails.moreInformationButton')}
                  </UtilButton>
                </Box>
              )}
            </Box>
          </Card>

          {ctx.values.Contact !== '' && (
            <>
              {isNewContact && (
                <>
                  <Card title={t('ContactDetails.title')}>
                    <div className={classes('grid')}>{renderFormElements({ config: contactDetailsRows })}</div>
                  </Card>

                  <Card title={t('ContactAddress.title')}>
                    <div className={classes('grid')}>{renderFormElements({ config: contactAddressRows })}</div>
                  </Card>
                </>
              )}

              <Card title={t('ContactPaymentSettings.title')}>
                <div className={classes('grid')}>{renderFormElements({ config: paymentSettingsRows })}</div>

                {ctx.values.PaymentSelectionMethod?.includes('bank') && formType !== 'Connect' && (
                  <div className={classes('bankInfoContainer')}>
                    <Card title={t('BankAccount.title')}>
                      <div className={classes('grid')}>{renderFormElements({ config: bankAccountRows })}</div>
                    </Card>
                  </div>
                )}

                {(ctx.values.PaymentSelectionMethod?.includes('credit') ||
                  ctx.values.PaymentSelectionMethod?.includes('debitcard')) && (
                  <div className={classes('bankInfoContainer')}>
                    <Card title={t('CardSettings.title', { defaultValue: 'Card Settings' })}>
                      <div className={classes('grid')}>{renderFormElements({ config: cardSettingsRows })}</div>
                    </Card>
                  </div>
                )}
              </Card>

              {formType == 'Flex' && (
                <Tooltip
                  information={t('MultiplePaymentMethods.disabledTooltip')}
                  hasButton={false}
                  bgColor="transparent"
                  customElement={
                    <Box flx="col" width="100%">
                      <CollapsibleCard
                        onOpenChange={(open) => {
                          setHasMultiplePaymentMethods(open);
                        }}
                        open={hasMultiplePaymentMethods}
                        disabled={!(!isUnderReview && (ctx.values.PaymentSelectionMethod || '').split(',').length > 1)}
                      >
                        <CollapsibleCard.Trigger data-testid="multiplePayments-trigger">
                          <Box gapSize="5px" ai="center">
                            <span>{t('MultiplePaymentMethods.title')}</span>
                            <Tooltip
                              information={t('MultiplePaymentMethods.enabledTooltip')}
                              position="top"
                              width="394px"
                              buttonWidth="20px"
                              isDisabled={!ctx.values.PaymentSelectionMethod}
                            />
                          </Box>
                        </CollapsibleCard.Trigger>

                        <CollapsibleCard.Content className={styles.noBody}>
                          <></>
                        </CollapsibleCard.Content>
                      </CollapsibleCard>
                    </Box>
                  }
                  isDisabled={(ctx.values.PaymentSelectionMethod || '').split(',').length > 1}
                  position="top"
                  width="394px"
                  buttonWidth="20px"
                />
              )}

              {formType == 'Flex' && (
                <CollapsibleCard
                  onOpenChange={(open) => {
                    setIsPartialPayment(open);
                  }}
                  open={isPartialPayment}
                  disabled={isUnderReview}
                >
                  <CollapsibleCard.Trigger data-testid="partialPayment-trigger">
                    <Box gapSize="5px" ai="center">
                      <span>{t('PartialPayment.title')}</span>
                      <Tooltip
                        information={t('PartialPayment.tooltip')}
                        position="top"
                        width="394px"
                        buttonWidth="20px"
                      />
                    </Box>
                  </CollapsibleCard.Trigger>

                  <CollapsibleCard.Content>
                    <div className={classes('grid')}>{renderFormElements({ config: partialPaymentRows })}</div>
                  </CollapsibleCard.Content>
                </CollapsibleCard>
              )}
              {formType == 'Flex' && (
                <CollapsibleCard
                  onOpenChange={(open) => {
                    setIsInstallmentPlan(open);
                  }}
                  open={isInstallmentPlan}
                  disabled={isUnderReview}
                >
                  <CollapsibleCard.Trigger data-testid="installmentPlan-trigger">
                    <Box gapSize="5px" ai="center">
                      <span>{t('InstallmentPlan.title')}</span>
                      <Tooltip
                        information={t('InstallmentPlan.tooltip')}
                        position="top"
                        width="394px"
                        buttonWidth="20px"
                      />
                    </Box>
                  </CollapsibleCard.Trigger>

                  <CollapsibleCard.Content>
                    <div className={classes('grid')}>{renderFormElements({ config: installmentPlansRows })}</div>
                  </CollapsibleCard.Content>
                </CollapsibleCard>
              )}

              <Card title={t('ELinxDetails.title')}>
                <div className={classes('grid', 'spanLastChild')}>{renderFormElements({ config: detailsRows })}</div>
              </Card>

              <Card title={t('ELinxSettings.title')}>
                <div className={classes('grid')}>{renderFormElements({ config: settingsRows })}</div>
              </Card>
            </>
          )}
        </Box>
        <Box jc="sb" mt="lg" base="8">
          <Box jc="fs">
            <Button
              iconRight={!lockedFeatures.PayLinkEnabled || !permissions.ViewElinxSettings ? 'lock' : undefined}
              onClick={() => {
                !lockedFeatures.PayLinkEnabled || !permissions.ViewElinxSettings
                  ? updateIsLocked(true, 'Elinx')
                  : setIsVisible(true);
              }}
            >
              {t('Buttons.editElinxSettings')}
            </Button>
          </Box>
          <Box flx="row" gap="common">
            <Box jc="fe">
              <Button onClick={() => setIsPreviewing(true)}>{t('Buttons.seePreview')}</Button>
            </Box>
            <Box jc="fe" gap="sm">
              {isUnderReview ? (
                <Button
                  onClick={() => {
                    setIsUnderReview(false);
                  }}
                >
                  {t('Buttons.back')}
                </Button>
              ) : null}
              {!isUnderReview && (
                <Button
                  colorScheme="primary"
                  onClick={() => {
                    setIsUnderReview(true);
                    ref.current?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  disabled={!ctx.isValid || Object.values(ctx.errors).length > 0}
                >
                  {t('Buttons.review')}
                </Button>
              )}
              {isUnderReview && (
                <Button
                  type="submit"
                  disabled={!ctx.isValid || Object.values(ctx.errors).length > 0}
                  colorScheme="primary"
                >
                  {t('Buttons.submit')}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </form>

      <ElinxSettingsDialogue
        isVisible={isVisible}
        onCloseModal={() => setIsVisible(false)}
        onFetchElinxSettings={getElinxSettings}
      />
      <ElinxPreview visible={isPreviewing} onClose={() => setIsPreviewing(false)} />
    </div>
  );
};

export default ElinxForm;
